import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SalesAgentCustomersTab = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    postal_code: '',
    city: '',
    property_type: '',
    notes: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user?.id) {
      loadCustomers();
    }
  }, [user]);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        ?.from('crm_customers')
        ?.select('*')
        ?.eq('provider_id', user?.id)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      setCustomers(data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.customer_name?.trim()) newErrors.customer_name = 'Naam is verplicht';
    if (!formData?.customer_email?.trim()) newErrors.customer_email = 'E-mail is verplicht';
    if (!formData?.customer_phone?.trim()) newErrors.customer_phone = 'Telefoon is verplicht';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleAddCustomer = async () => {
    if (!validateForm()) return;

    try {
      const { data, error } = await supabase
        ?.from('crm_customers')
        ?.insert([{
          provider_id: user?.id,
          customer_name: formData?.customer_name,
          customer_email: formData?.customer_email,
          customer_phone: formData?.customer_phone,
          customer_address: formData?.customer_address,
          postal_code: formData?.postal_code,
          city: formData?.city,
          property_type: formData?.property_type,
          notes: formData?.notes,
        }])
        ?.select();

      if (error) throw error;

      setCustomers(prev => [data?.[0], ...prev]);
      setShowAddModal(false);
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        postal_code: '',
        city: '',
        property_type: '',
        notes: '',
      });
      alert('Klant succesvol toegevoegd!');
    } catch (error) {
      console.error('Error adding customer:', error);
      alert('Fout bij toevoegen klant');
    }
  };

  const propertyTypeOptions = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'terraced', label: 'Rijtjeshuis' },
    { value: 'semi-detached', label: 'Twee-onder-een-kap' },
    { value: 'detached', label: 'Vrijstaand huis' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">Klantenbeheer</h2>
          <p className="text-muted-foreground mt-1">Voeg klanten toe en beheer hun gegevens</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <Icon name="Plus" size={16} className="mr-2" />
          Klant toevoegen
        </Button>
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-heading font-bold text-foreground">Nieuwe Klant</h3>
              <button onClick={() => setShowAddModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Naam"
                  type="text"
                  placeholder="Volledige naam"
                  value={formData?.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e?.target?.value)}
                  error={errors?.customer_name}
                  required
                />
                <Input
                  label="E-mailadres"
                  type="email"
                  placeholder="email@voorbeeld.nl"
                  value={formData?.customer_email}
                  onChange={(e) => handleInputChange('customer_email', e?.target?.value)}
                  error={errors?.customer_email}
                  required
                />
                <Input
                  label="Telefoonnummer"
                  type="tel"
                  placeholder="06 12345678"
                  value={formData?.customer_phone}
                  onChange={(e) => handleInputChange('customer_phone', e?.target?.value)}
                  error={errors?.customer_phone}
                  required
                />
                <Input
                  label="Adres"
                  type="text"
                  placeholder="Straat en huisnummer"
                  value={formData?.customer_address}
                  onChange={(e) => handleInputChange('customer_address', e?.target?.value)}
                />
                <Input
                  label="Postcode"
                  type="text"
                  placeholder="1234 AB"
                  value={formData?.postal_code}
                  onChange={(e) => handleInputChange('postal_code', e?.target?.value)}
                />
                <Input
                  label="Plaats"
                  type="text"
                  placeholder="Amsterdam"
                  value={formData?.city}
                  onChange={(e) => handleInputChange('city', e?.target?.value)}
                />
              </div>
              <Select
                label="Woningtype"
                placeholder="Selecteer woningtype"
                options={propertyTypeOptions}
                value={formData?.property_type}
                onChange={(value) => handleInputChange('property_type', value)}
              />
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Notities</label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md text-foreground bg-background"
                  rows={3}
                  placeholder="Extra informatie over de klant"
                  value={formData?.notes}
                  onChange={(e) => handleInputChange('notes', e?.target?.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Annuleren
              </Button>
              <Button onClick={handleAddCustomer}>
                Klant toevoegen
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Customers List */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {customers?.length === 0 ? (
          <div className="p-12 text-center">
            <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nog geen klanten toegevoegd</p>
            <Button className="mt-4" onClick={() => setShowAddModal(true)}>
              Eerste klant toevoegen
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/30">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Naam
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Locatie
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Woningtype
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Toegevoegd
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {customers?.map((customer) => (
                  <tr key={customer?.id} className="hover:bg-muted/20">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-foreground">{customer?.customer_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground">{customer?.customer_email}</div>
                      <div className="text-xs text-muted-foreground">{customer?.customer_phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-foreground">{customer?.city || '-'}</div>
                      <div className="text-xs text-muted-foreground">{customer?.postal_code || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-foreground">
                      {customer?.property_type || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {new Date(customer?.created_at)?.toLocaleDateString('nl-NL')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesAgentCustomersTab;